import React, { useCallback, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import EntityList from "../EntityList/EntityList";
import { defineMessages, useIntl } from "react-intl";
import swal from "sweetalert";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import { PropertyConfigurationContext } from "../../../../components/property-configuration-provider";
import { isAgrCondition } from "./domain/agr.helpers";
import { useChainData } from "../../../../components/chain-data-provider";
import { deleteAgrCondition, cloneAgrCondition, downloadConditionsExcel } from "./infrastructure/conditionAgr.api";
import { createExcelFileDownloadFromRawData } from "../../../../lib/supportFunctions";

const messages = defineMessages({
  inventoriesWarning: {
    id: "app.conditionsList.inventoriesWarning",
    defaultMessage: "This condition is being used in some inventory lines, it can not be deleted.",
  },
  close: {
    id: "app.conditionsList.close",
    defaultMessage: "Close",
  },
  disabledDelete: {
    id: "app.conditionsList.tooltip.delete",
    defaultMessage: "Disabled delete",
  },
  sharedUse: {
    id: "conditions.list.use.shared",
    defaultMessage: "Shared",
  },
  multiple: {
    id: "conditions.tableValue.motors.multiple",
    defaultMessage: "Multiple",
  },
  deleteError: {
    id: "app.conditionsList.deleteError",
    defaultMessage: "An error occurred while deleting the condition. Please try again.",
  },
  cloneError: {
    id: "app.conditionsList.cloneError",
    defaultMessage: "Could not retrieve the aggregator condition data. The condition was not cloned.",
  },
});

const EMPTY_ARRAY = [];

function ConditionsList({
  onDeleteListItem,
  onCloneListItem,
  onToggleListItem,
  entities,
  entity,
  config,
  loading,
  entityError,
  entityResult,
  handleCreate,
  handleEdit,
  success,
  wbkData,
  hotelTicker,
}) {
  const history = useHistory();
  const intl = useIntl();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const propertyConfig = useContext(PropertyConfigurationContext);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const conditionsInUse = wbkData?.conditionsInUse || EMPTY_ARRAY;

  const { name, entityName, validation, fields, renderers, allowClone, restConfig } = config;

  const handleToggle = useCallback(
    ({ event, entity: rowEntity }) => {
      event.preventDefault();
      event.stopPropagation();
      onToggleListItem({ id: rowEntity.id, entity: rowEntity, activeProperty: "active" });
    },
    [onToggleListItem]
  );

  const handleDelete = useCallback(
    ({ event, entity: rowEntity }) => {
      event.preventDefault();
      event.stopPropagation();
      if (isChain) {
        const baseURL = (restConfig && restConfig.baseURL) || "";
        return deleteAgrCondition(baseURL, hotelTicker, rowEntity.ticker)
          .then(() => {
            history.push(`/${hotelTicker}/${entityName}/success`);
            // Full reload because EntityList's data comes from the legacy HOC
            // and there's no direct cache-invalidation hook.
            window.location.reload();
          })
          .catch(e => {
            console.error("Error deleting AGR condition", e);
            swal({ text: intl.formatMessage(messages.deleteError), icon: "warning" });
          });
      }
      return onDeleteListItem({ id: rowEntity.id, entity: rowEntity });
    },
    [isChain, onDeleteListItem, hotelTicker, entityName, restConfig, intl, history]
  );

  const handleClone = useCallback(
    async ({ entity: listEntity, ...rest }) => {
      if (!isChain || !listEntity?.ticker) {
        onCloneListItem({ entity: listEntity, ...rest });
        return;
      }
      try {
        const baseURL = (restConfig && restConfig.baseURL) || "";
        const cloneEntity = await cloneAgrCondition(baseURL, hotelTicker, listEntity.ticker);
        window.sessionStorage.setItem("duplicate", JSON.stringify(cloneEntity));
        window.sessionStorage.setItem("duplicateEntityName", entityName);
        history.push(`/${hotelTicker}/${entityName}/new`);
      } catch (error) {
        console.error("Error cloning AGR condition", error);
        swal({
          text: intl.formatMessage(messages.cloneError),
          icon: "warning",
          buttons: { cancel: intl.formatMessage(messages.close) },
        });
      }
    },
    [isChain, hotelTicker, entityName, restConfig, onCloneListItem, history, intl]
  );

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const propertyTickerValue = (entity && entity.hotelTicker) || hotelTicker;
      const baseURL = (restConfig && restConfig.baseURL) || "";
      const result = await downloadConditionsExcel(baseURL, propertyTickerValue);
      createExcelFileDownloadFromRawData(result.data, result.filename);
    } catch (e) {
      console.error("Error downloading conditions excel", e);
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [entity, hotelTicker, restConfig]);

  const computedFields = useMemo(() => {
    // PropertyConfiguration returns "1"/"0" strings for boolean flags (legacy backend)
    const onlineStoreActive = propertyConfig && propertyConfig.onlineStoreActive === "1";
    const maarlabActive = propertyConfig && propertyConfig.isMaarlabActive === "1";
    const listFields = (fields.list || []).filter(field => {
      // A single type column, worth showing only when the property can actually hold a
      // non-ENGINE condition (a store condition or a Maarlab Flight + Hotel one).
      if (field.fieldName === "conditionType") return Boolean(onlineStoreActive || maarlabActive);
      if (field.fieldName === "motorsList") return isChain;
      if (field.fieldName === "uso") return !isChain;
      return true;
    });
    return { ...fields, list: listFields };
  }, [fields, isChain, propertyConfig]);

  const customEntityToListValuesMapper = useCallback(
    ({ entities: entitiesToMap, currentLocale }) =>
      entitiesToMap.map(oldEntity => {
        const mappedEntity = { ...oldEntity };

        const currentTranslation =
          mappedEntity.translations &&
          mappedEntity.translations.find(t => t.locale === currentLocale);
        mappedEntity.name = currentTranslation ? currentTranslation.name : mappedEntity.name;
        // conditionType is left as the raw enum: the column renders it as a label via
        // optionDisplay (see ModuleDefinition), so there is nothing to coerce.
        mappedEntity.isShared = isAgrCondition(mappedEntity);

        const childTickers = Array.isArray(mappedEntity.childPropertyTickers)
          ? mappedEntity.childPropertyTickers
          : Array.isArray(mappedEntity.motorsList) ? mappedEntity.motorsList : [];

        mappedEntity.uso = mappedEntity.isShared ? intl.formatMessage(messages.sharedUse) : "";

        if (childTickers.length === 0) {
          mappedEntity.motorsList = "";
        } else if (childTickers.length === 1) {
          mappedEntity.motorsList = childTickers[0];
        } else {
          mappedEntity.motorsList = intl.formatMessage(messages.multiple);
        }

        const inUse = conditionsInUse.includes(mappedEntity.ticker);
        const isAgrInIndividual = !isChain && mappedEntity.isShared;
        mappedEntity.allowDelete = !inUse && !isAgrInIndividual;

        return mappedEntity;
      }),
    [intl, isChain, conditionsInUse]
  );

  const customActions = useMemo(() => [
      {
        classNames: "btn-danger disabled",
        icon: "fa fa-trash-o",
        tooltipId: "delete-disabled",
        tooltipText: intl.formatMessage(messages.disabledDelete),
        onExecute: () => {
          swal({
            text: intl.formatMessage(messages.inventoriesWarning),
            icon: "warning",
            buttons: { cancel: intl.formatMessage(messages.close) },
            dangerMode: true,
          });
        },
        isAvailable: ({ entity: rowEntity }) =>
          !rowEntity.isShared && conditionsInUse.includes(rowEntity.ticker),
      },
    ],
  [intl, conditionsInUse]);

  return (
    <EntityList
      loading={loading}
      entityName={entityName}
      entities={entities}
      validation={validation}
      renderers={renderers}
      name={name}
      fields={computedFields}
      onToggle={handleToggle}
      onDelete={handleDelete}
      onClone={handleClone}
      entityError={entityError}
      entityResult={entityResult}
      handleCreate={handleCreate}
      handleEdit={handleEdit}
      customEntityToListValuesMapper={customEntityToListValuesMapper}
      currentLocale={config.locale}
      success={success}
      allowClone={allowClone}
      isDownloadingExcel={isDownloadingExcel}
      onDownloadExcel={isChain ? handleDownloadExcel : undefined}
      customActions={customActions}
    />
  );
}

ConditionsList.defaultProps = {
  entities: [],
};

export default withPropertyData(ConditionsList);
